import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { StaleSessionNotice } from "@/features/account/components/StaleSessionNotice";
import { Dialog } from "@/components/ui/Dialog";

export const REASON_MIN_LENGTH = 10;
export const REASON_MAX_LENGTH = 500;

interface OpenContextDialogProps {
  open: boolean;
  busy: boolean;
  targetEmail: string;
  error?: string;
  staleSession?: boolean;
  staleSessionBody?: string;
  staleSessionReturnTo?: string;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export function OpenContextDialog({
  open,
  busy,
  targetEmail,
  error,
  staleSession = false,
  staleSessionBody,
  staleSessionReturnTo = "/staff",
  onConfirm,
  onCancel,
}: OpenContextDialogProps) {
  const { t } = useTranslation();
  const descriptionId = useId();
  const consequenceId = useId();
  const [reason, setReason] = useState("");
  const tooShort = reason.trim().length < REASON_MIN_LENGTH;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={t("staff.openContext.title")}
      describedBy={`${descriptionId} ${consequenceId}`}
      error={staleSession ? undefined : error}
    >
      <p id={descriptionId} className="text-sm text-muted">
        {t("staff.openContext.description", { email: targetEmail })}
      </p>
      <p id={consequenceId} className="mt-2 text-sm text-muted">
        {t("staff.openContext.consequence")}
      </p>
      <label className="mt-4 block text-sm font-medium text-heading" htmlFor="support-reason">
        {t("staff.openContext.reasonLabel")}
      </label>
      <textarea
        id="support-reason"
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        maxLength={REASON_MAX_LENGTH}
        rows={3}
        readOnly={busy || staleSession}
        aria-disabled={busy || staleSession || undefined}
        data-autofocus={busy || staleSession ? undefined : true}
        className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-body aria-disabled:opacity-50"
        placeholder={t("staff.openContext.reasonPlaceholder")}
      />
      <p className="mt-1 text-xs text-subtle">
        {t("staff.openContext.reasonHint", { min: REASON_MIN_LENGTH })}
      </p>
      {staleSession && (
        <StaleSessionNotice
          returnTo={staleSessionReturnTo}
          body={staleSessionBody}
          className="mt-4"
          autoFocus
        />
      )}
      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted hover:bg-elevated hover:text-heading"
        >
          {t("common.cancel")}
        </button>
        <button
          type="button"
          onClick={() => {
            if (busy || staleSession) return;
            onConfirm(reason.trim());
          }}
          disabled={(!busy && tooShort) || staleSession}
          aria-disabled={busy || staleSession || undefined}
          aria-busy={busy || undefined}
          data-autofocus={busy && !staleSession ? true : undefined}
          className="rounded-lg bg-indigo px-4 py-2 text-sm font-medium text-white hover:bg-indigo-bright disabled:opacity-60 aria-disabled:opacity-60 aria-disabled:cursor-not-allowed"
        >
          {busy ? t("staff.openContext.opening") : t("staff.openContext.confirm")}
        </button>
      </div>
    </Dialog>
  );
}
